import React, { Fragment } from 'react';

import { type MDXProviderComponents } from '@mdx-js/react';
import { MDXProvider } from '@mdx-js/react';
import { MDXRenderer } from 'gatsby-plugin-mdx';
// @ts-expect-error
import { slug as slugify } from 'github-slugger';

import { CodeDocsHeader, SectionHeadingLink } from '@af/design-system-docs-ui';
import { Code, CodeBlock } from '@atlaskit/code';
import Heading, { HeadingContextProvider } from '@atlaskit/heading';
import { Box, xcss } from '@atlaskit/primitives';

import useSnippet, { type SnippetProps } from '../../../hooks/use-snippet';
import type Props from '../components/props';
import { TSMorphProps } from '../components/props';
import isValidPropsDefinition from '../utilities/is-valid-props-definition';

import ContentRenderer from './content-renderer';

type CodeComponentProps = { children: string };

// this is used to generate the local nav headings on code pages
export const codeDocsHeadings = [
	{
		value: 'Installation',
		id: 'installation',
		depth: 2,
	},
];

type Props = {
	workspaceInfo: any;
	propsMdx?: any;
};

/**
 * __Code docs__
 */
const CodeDocs = ({ workspaceInfo, propsMdx }: Props) => {
	return (
		<HeadingContextProvider>
			<CodeDocsHeader
				name={workspaceInfo.name}
				group={workspaceInfo.group}
				repository={workspaceInfo.repository}
				directoryName={workspaceInfo.directoryName}
			/>
			<CodeDocsPropsSection propsMdx={propsMdx} definition={workspaceInfo.definition} />
		</HeadingContextProvider>
	);
};

const components: MDXProviderComponents = {
	h2: ({ children }: CodeComponentProps) => (
		<SectionHeadingLink id={slugify(children)} depth={2}>
			{children}
		</SectionHeadingLink>
	),
	h3: ({ children }: CodeComponentProps) => (
		<SectionHeadingLink id={slugify(children)} depth={3}>
			{children}
		</SectionHeadingLink>
	),
	h4: ({ children }: CodeComponentProps) => (
		<Box xcss={h4MarginStyles}>
			<Heading size="small">{children}</Heading>
		</Box>
	),
	h5: ({ children }: CodeComponentProps) => (
		<Box xcss={h5MarginStyles}>
			<Heading size="xsmall">{children}</Heading>
		</Box>
	),
	code: ({ children }: CodeComponentProps) => (
		<CodeBlock text={children} language="tsx" showLineNumbers={false} />
	),
	inlineCode: ({ children }) => <Code>{children}</Code>,
	Snippet: ({ name, variables }: SnippetProps) => {
		const snippet = useSnippet(name, variables);
		return <ContentRenderer body={snippet} id={name} />;
	},
};

interface CodeDocsPropsSectionProps {
	propsMdx: any;
	definition: any;
}

const CodeDocsPropsSection = ({ propsMdx, definition }: CodeDocsPropsSectionProps) => {
	if (propsMdx) {
		const exportPropTypes =
			typeof propsMdx?.exportPropTypes === 'string'
				? JSON.parse(propsMdx.exportPropTypes)
				: propsMdx?.exportPropTypes;

		// TODO: Add `type` prop with default being component
		components.TSMorphProps = ({ packageName, exportName, filter }) => {
			return (
				<TSMorphProps
					props={exportPropTypes?.[packageName]?.[exportName]}
					filter={filter}
					componentDisplayName={exportName}
				/>
			);
		};

		return (
			<MDXProvider components={components}>
				<MDXRenderer>{propsMdx.body}</MDXRenderer>
			</MDXProvider>
		);
	} else if (isValidPropsDefinition(definition)) {
		/**
		 * There is no props.mdx, but there is prop info from ERT.
		 */
		const propsContent = 'Props';

		return (
			<Fragment>
				<SectionHeadingLink id="props" depth={2}>
					{propsContent}
				</SectionHeadingLink>
				{/* JSON.parsing JSON string input, ideally this payload should be a typed object in gql */}
				<TSMorphProps props={JSON.parse(definition)} />
			</Fragment>
		);
	}

	/**
	 * If there is no prop info, we don't want to show a props section.
	 */
	return null;
};

export default CodeDocs;

const h4MarginStyles = xcss({
	marginBlockStart: 'space.300',
});

const h5MarginStyles = xcss({
	marginBlockStart: 'space.200',
});
